import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { GrLoading } from "../feedback";
import { palette } from "../../theme/palette";
import { typography } from "../../theme/typography";
import styled from "@emotion/styled";
import classNames from "classnames";
import { GrIcon } from "../icons/GrIcon";
const StyledButton = styled("button")(({ size, color, variant, startIcon, isLoading, disabled }) => {
    return {
        display: "inline-flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: "6px",
        border: "none",
        gap: "10px",
        color: palette.white,
        cursor: disabled || isLoading ? "not-allowed" : "pointer",
        ...(size === "large" && {
            padding: "10px 16px",
            height: "40px",
        }),
        ...(size === "medium" && {
            padding: "6px 12px",
            height: "32px",
        }),
        ...(size === "small" && {
            padding: "3px 10px",
            height: "24px",
        }),
        ".GrButton-content": {
            position: "relative",
            top: "1px",
            padding: "0 2px",
            ...(isLoading &&
                !startIcon && {
                color: "transparent",
                position: "relative",
            }),
            ...(size === "large" && {
                ...typography["Content.Subtitle02"],
            }),
            ...(size === "medium" && {
                ...typography["Content.Subtitle03"],
            }),
            ...(size === "small" && {
                ...typography["Content.Subtitle04"],
            }),
        },
        ".GrButton-loading": {
            ...(isLoading &&
                !startIcon && {
                position: "absolute",
            }),
        },
        ...(color === "primary" && {
            ...(variant === "contained" && {
                backgroundColor: palette.blue050,
                "&:hover": {
                    backgroundColor: "#1F81A5",
                },
                "&:active": {
                    backgroundColor: "#1A6C8A",
                },
            }),
            ...(variant === "outlined" && {
                backgroundColor: palette.white,
                color: palette.blue050,
                border: `1px solid ${palette.blue050}`,
                "&:hover": {
                    backgroundColor: "#E9F5F9",
                },
                "&:active": {
                    backgroundColor: "#A8D7E8",
                },
            }),
            ...(variant === "text" && {
                backgroundColor: "transparent",
                color: palette["maven-blue-dark"],
                "&:hover": {
                    backgroundColor: "#E9F5F9",
                },
                "&:active": {
                    backgroundColor: "#A8D7E8",
                },
            }),
        }),
        ...(color === "grey" && {
            ...(variant === "contained" && {
                backgroundColor: palette.grey020,
                color: palette.grey100,
                "&:hover": {
                    backgroundColor: "#D8DADC",
                },
                "&:active": {
                    backgroundColor: "#C0C1C3",
                },
            }),
            ...(variant === "outlined" && {
                backgroundColor: palette.white,
                color: palette.grey100,
                border: `1px solid ${palette.grey025}`,
                "&:hover": {
                    backgroundColor: "#F5F6F7",
                },
                "&:active": {
                    backgroundColor: "#E9EBED",
                },
            }),
            ...(variant === "text" && {
                backgroundColor: "transparent",
                color: palette.grey100,
                "&:hover": {
                    backgroundColor: "#F5F6F7",
                },
                "&:active": {
                    backgroundColor: "#E9EBED",
                },
            }),
        }),
        ...(color === "orange" && {
            ...(variant === "contained" && {
                backgroundColor: palette.orange050,
                "&:hover": {
                    backgroundColor: "#D3600E",
                },
                "&:active": {
                    backgroundColor: "#B0500C",
                },
            }),
            ...(variant === "outlined" && {
                backgroundColor: palette.white,
                color: palette.orange050,
                border: `1px solid ${palette.orange050}`,
                "&:hover": {
                    backgroundColor: "#FFF3EA",
                },
                "&:active": {
                    backgroundColor: "#FFDBC1",
                },
            }),
            ...(variant === "text" && {
                backgroundColor: "transparent",
                color: palette["warning-primary"],
                "&:hover": {
                    backgroundColor: "#FFF3EA",
                },
                "&:active": {
                    backgroundColor: "#FFDBC1",
                },
            }),
        }),
        ...(color === "red" && {
            ...(variant === "contained" && {
                backgroundColor: palette.red050,
                "&:hover": {
                    backgroundColor: "#D24148",
                },
                "&:active": {
                    backgroundColor: "#AF363C",
                },
            }),
            ...(variant === "outlined" && {
                backgroundColor: palette.white,
                color: palette.red050,
                border: `1px solid ${palette.red040}`,
                "&:hover": {
                    backgroundColor: "#FFF0F1",
                },
                "&:active": {
                    backgroundColor: "#FFDADC",
                },
            }),
            ...(variant === "text" && {
                backgroundColor: "transparent",
                color: palette.red050,
                "&:hover": {
                    backgroundColor: "#FFF0F1",
                },
                "&:active": {
                    backgroundColor: "#FFDADC",
                },
            }),
        }),
        ...(color === "green" && {
            ...(variant === "contained" && {
                backgroundColor: palette.green050,
                "&:hover": {
                    backgroundColor: "#1E873C",
                },
                "&:active": {
                    backgroundColor: "#16652D",
                },
            }),
            ...(variant === "outlined" && {
                backgroundColor: palette.white,
                color: palette.green050,
                border: `1px solid ${palette.green040}`,
                "&:hover": {
                    backgroundColor: "#ECF8F0",
                },
                "&:active": {
                    backgroundColor: "#C6ECD1",
                },
            }),
            ...(variant === "text" && {
                backgroundColor: "transparent",
                color: palette.green050,
                "&:hover": {
                    backgroundColor: "#ECF8F0",
                },
                "&:active": {
                    backgroundColor: "#C6ECD1",
                },
            }),
        }),
        ...(color === "white" && {
            ...(variant === "text" && {
                backgroundColor: "transparent",
                color: palette.white,
                "&:hover": {
                    backgroundColor: "#F5F6F7",
                },
                "&:active": {
                    backgroundColor: "#E9EBED",
                },
            }),
        }),
        ...((disabled || isLoading) && {
            backgroundColor: palette.grey020,
            color: palette.grey030,
            border: `1px solid ${palette.grey025}`,
            cursor: "not-allowed",
            "&:hover": {
                backgroundColor: palette.grey020,
                color: palette.grey030,
                border: `1px solid ${palette.grey025}`,
                cursor: "not-allowed",
            },
            "&:active": {
                backgroundColor: palette.grey020,
                color: palette.grey025,
                border: `1px solid ${palette.grey025}`,
                cursor: "not-allowed",
            },
        }),
    };
});
export function GrButton(props) {
    const { size = "medium", color = "primary", variant = "contained", children, startIcon, startIconSize, endIcon, endIconSize, isLoading, fullWidth, disabled = false, className, contentStyle, onClick, type, ...others } = props;
    const handleClick = (e) => {
        if (!disabled && !isLoading && onClick) {
            onClick(e);
        }
    };
    return (_jsxs(StyledButton, { role: "button", type: type, className: classNames("GrButton", className), size: size, color: color, variant: variant, startIcon: startIcon, endIcon: endIcon, isLoading: isLoading, fullWidth: fullWidth, disabled: disabled, onClick: handleClick, ...others, children: [!isLoading && startIcon && (_jsx(GrIcon, { type: startIcon, size: startIconSize ? startIconSize : size === "small" ? 14 : 16, color: getIconColor(color, variant, !!disabled || !!isLoading) })), isLoading && (_jsx("div", { className: "GrButton-loading", children: _jsx(GrLoading, { size: size === "small" ? 14 : 16 }) })), _jsx("div", { className: "GrButton-content", style: contentStyle, children: children }), endIcon && (_jsx(GrIcon, { type: endIcon, size: endIconSize ? endIconSize : size === "small" ? 14 : 16, color: getIconColor(color, variant, !!disabled || !!isLoading) }))] }));
}
function getIconColor(color, variant, disabled) {
    if (disabled)
        return "grey025";
    if (color === "grey")
        return "grey070";
    if (variant === "contained" || color === "white")
        return "white";
    if (color === "primary")
        return "maven-blue-dark";
    if (color === "orange")
        return "warning-primary";
    if (color === "red")
        return "negative-primary";
    if (color === "green")
        return "positive-primary";
    return "white";
}
//# sourceMappingURL=GrButton.js.map